<template>
  <div>
    <b-card no-body class="mb-3 filters-card">
      <div class="p-3">
        <b-form @submit.prevent="applyFilters">
          <div class="filter-grid">
            <div class="filter-field">
              <label class="filter-label">ผู้ถูกกระทำ</label>
              <multiselect
                v-model="filters.target"
                :options="userOptions"
                :loading="isLoadingMeta"
                label="label"
                track-by="uid"
                placeholder="เลือกผู้ใช้"
                :show-labels="false"
                :allow-empty="true"
                :searchable="true"
              >
                <template #noResult>ไม่พบผู้ใช้</template>
                <template #noOptions>ไม่มีผู้ใช้</template>
              </multiselect>
            </div>

            <div class="filter-field">
              <label class="filter-label">ผู้ดำเนินการ</label>
              <multiselect
                v-model="filters.by"
                :options="userOptions"
                :loading="isLoadingMeta"
                label="label"
                track-by="uid"
                placeholder="เลือกผู้ใช้"
                :show-labels="false"
                :allow-empty="true"
                :searchable="true"
              >
                <template #noResult>ไม่พบผู้ใช้</template>
                <template #noOptions>ไม่มีผู้ใช้</template>
              </multiselect>
            </div>

            <div class="filter-field">
              <label class="filter-label">สาขา</label>
              <multiselect
                v-model="filters.branch"
                :options="branchOptions"
                label="name"
                track-by="id"
                placeholder="เลือกสาขา"
                :show-labels="false"
                :allow-empty="true"
                :searchable="true"
              >
                <template #noResult>ไม่พบสาขา</template>
                <template #noOptions>ไม่มีสาขา</template>
              </multiselect>
            </div>

            <div class="filter-field">
              <label class="filter-label">ตั้งแต่</label>
              <b-form-input v-model="filters.fromDt" type="date" size="sm" />
            </div>

            <div class="filter-field">
              <label class="filter-label">ถึง</label>
              <b-form-input v-model="filters.toDt" type="date" size="sm" />
            </div>

            <div class="filter-actions">
              <b-button type="submit" size="sm" variant="primary">
                ค้นหา
              </b-button>
              <b-button
                size="sm"
                variant="outline-secondary"
                @click="clearFilters"
              >
                ล้าง
              </b-button>
            </div>
          </div>
        </b-form>
      </div>
    </b-card>

    <b-card no-body>
      <div v-if="isLoading" class="text-center py-5">
        <b-spinner class="align-middle" />
      </div>
      <div v-else-if="logs.length === 0" class="p-4 text-muted text-center">
        ไม่มีประวัติ
      </div>
      <div v-else class="p-3">
        <AuditTimeline
          :logs="logs"
          :user-map="userMap"
          :branch-map="branchMap"
          show-target
          @target-click="openUser"
        />
      </div>

      <div class="p-3 border-top d-flex justify-content-between align-items-center">
        <small class="text-muted">
          แสดง {{ logs.length }} จาก {{ total }} รายการ
        </small>
        <b-pagination
          v-model="page"
          :total-rows="total"
          :per-page="limit"
          align="right"
          size="sm"
          @change="onPageChange"
        />
      </div>
    </b-card>

    <UserDetailModal ref="UserDetailModal" @updated="refresh" />
  </div>
</template>

<script>
import axios from "axios";
import { mapGetters, mapActions } from "vuex";
import UserDetailModal from "@/components/user-mgmt/UserDetailModal.vue";
import AuditTimeline from "@/components/user-mgmt/AuditTimeline.vue";

export default {
  name: "AuditLog",
  components: {
    UserDetailModal,
    AuditTimeline,
  },
  data() {
    return {
      logs: [],
      total: 0,
      page: 1,
      limit: 50,
      isLoading: false,
      isLoadingMeta: false,
      users: [],
      filters: {
        target: null,
        by: null,
        branch: null,
        fromDt: "",
        toDt: "",
      },
    };
  },
  computed: {
    ...mapGetters({ getClinicList: "moduleUser/getClinicList" }),
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    userOptions() {
      return this.users.map(u => ({
        uid: u.uid,
        label: `${u.firstNameTh || ""} ${u.lastNameTh || ""}`.trim() || `UID ${u.uid}`,
      }));
    },
    userMap() {
      const map = {};
      this.users.forEach(u => {
        map[u.uid] = `${u.firstNameTh || ""} ${u.lastNameTh || ""}`.trim() || `UID ${u.uid}`;
      });
      return map;
    },
    currentClinic() {
      return (this.getClinicList || []).find(c => c.code === this.clinicUrl);
    },
    branchOptions() {
      return (this.currentClinic?.branches || []).map(b => ({
        id: b.id,
        name: b.name,
      }));
    },
    branchMap() {
      const map = {};
      (this.currentClinic?.branches || []).forEach(b => {
        map[b.id] = b.name;
      });
      return map;
    },
  },
  async created() {
    await this.ensurePermissionContext();
    await Promise.all([this.fetchClinicList().catch(() => {}), this.loadMeta()]);
    await this.refresh();
  },
  methods: {
    ...mapActions({ fetchClinicList: "moduleUser/fetchClinicList" }),
    openUser(uid) {
      this.$refs.UserDetailModal.show({ clinicUrl: this.clinicUrl, uid });
    },
    async ensurePermissionContext() {
      // ensurePermissionContextFor handles its own cache check by clinicURL —
      // re-loads only when the active context belongs to a different clinic.
      try {
        await this.$store.dispatch("moduleUser/ensurePermissionContextFor", {
          clinicURL: this.clinicUrl,
        });
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("loadPermissionContext failed", err);
      }
    },
    async loadMeta() {
      this.isLoadingMeta = true;
      try {
        const res = await axios.get(`/api/userMgmt/${this.clinicUrl}/users`);
        this.users = Array.isArray(res.data?.users) ? res.data.users : [];
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load users for filter", err);
        this.users = [];
      } finally {
        this.isLoadingMeta = false;
      }
    },
    buildParams() {
      const params = {
        limit: this.limit,
        offset: (this.page - 1) * this.limit,
      };
      const f = this.filters;
      if (f.target?.uid) params.targetUid = f.target.uid;
      if (f.by?.uid) params.byUid = f.by.uid;
      if (f.branch?.id) params.branchId = f.branch.id;
      if (f.fromDt) params.fromDt = f.fromDt;
      if (f.toDt) params.toDt = f.toDt;
      return params;
    },
    async refresh() {
      this.isLoading = true;
      try {
        const res = await axios.get(
          `/api/userMgmt/${this.clinicUrl}/audit-log`,
          { params: this.buildParams() }
        );
        const data = res.data || {};
        this.logs = Array.isArray(data.logs) ? data.logs : [];
        this.total = data.total || 0;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("Failed to load audit log", err);
        this.logs = [];
        this.total = 0;
      } finally {
        this.isLoading = false;
      }
    },
    applyFilters() {
      this.page = 1;
      this.refresh();
    },
    clearFilters() {
      this.filters = {
        target: null,
        by: null,
        branch: null,
        fromDt: "",
        toDt: "",
      };
      this.page = 1;
      this.refresh();
    },
    onPageChange(newPage) {
      this.page = newPage;
      this.refresh();
    },
  },
};
</script>

<style scoped>
.filters-card {
  border-color: #e6eaf0;
}

.filter-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 0.75rem;
  align-items: flex-end;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.filter-label {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #6b7280;
  font-weight: 600;
  margin: 0;
}

.filter-actions {
  display: flex;
  gap: 0.5rem;
  align-items: flex-end;
}

/* Tighten multiselect to match b-form-input size="sm" */
.filter-field >>> .multiselect {
  min-height: 31px;
  font-size: 0.85rem;
}
.filter-field >>> .multiselect__tags {
  min-height: 31px;
  padding: 4px 36px 0 8px;
  font-size: 0.85rem;
}
.filter-field >>> .multiselect__single,
.filter-field >>> .multiselect__placeholder {
  font-size: 0.85rem;
  padding-top: 1px;
  margin-bottom: 4px;
}
.filter-field >>> .multiselect__select {
  height: 30px;
}
</style>
